import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  clips,
  scoreAccionesClips,
  reglasClips,
  globalStatsClips,
  fuenteLabels,
  duracionLabels,
  getMasVistos,
  getMasCompartidos,
  getClipsEnTendencia,
  formatearNumero,
  type Clip,
} from '@/mocks/clips';

type TabClips = 'mas-vistos' | 'mas-compartidos' | 'tendencia' | 'reglas';

function ClipCard({ clip }: { clip: Clip }) {
  const fuenteInfo = fuenteLabels[clip.fuente];
  const durInfo = duracionLabels[clip.duracion];

  return (
    <div className="rounded-2xl bg-background-50 border border-background-200/70 hover:border-rose-200/60 transition-all duration-300 overflow-hidden group cursor-pointer">
      <div className="relative h-40 overflow-hidden">
        <img
          src={clip.imagenUrl}
          alt={clip.titulo}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

        {/* Play overlay */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
            <i className="ri-play-fill text-foreground-950 text-xl ml-0.5"></i>
          </div>
        </div>

        {/* Duration badge */}
        <div className="absolute top-2.5 right-2.5">
          <span className={`px-2 py-0.5 rounded-md ${durInfo.bg} ${durInfo.color} text-[9px] font-bold`}>
            {durInfo.label}
          </span>
        </div>

        {/* Trending badge */}
        {clip.esTendencia && (
          <div className="absolute top-2.5 left-2.5">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              TENDENCIA
            </span>
          </div>
        )}

        {/* Source badge */}
        <div className="absolute bottom-2.5 left-2.5">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${fuenteInfo.color} text-[9px] font-semibold`}>
            <i className={`${fuenteInfo.icono} text-[9px]`}></i>
            {fuenteInfo.label}
          </span>
        </div>
      </div>

      <div className="p-3 space-y-2">
        <p className="text-foreground-950 font-bold text-xs leading-snug line-clamp-2">{clip.titulo}</p>

        {/* Radio & Host */}
        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
          <span className="flex items-center gap-1">
            <img src={clip.radioLogo} alt="" className="w-4 h-4 rounded-full object-cover" />
            {clip.radioNombre}
          </span>
          {clip.locutorNombre && (
            <>
              <span className="text-foreground-300">·</span>
              <span>{clip.locutorNombre}</span>
            </>
          )}
        </div>

        {/* Stats footer */}
        <div className="flex items-center gap-3 pt-2 border-t border-background-100 text-[10px]">
          <span className="text-foreground-400 flex items-center gap-1">
            <i className="ri-eye-line text-[10px]"></i>
            {formatearNumero(clip.vistas)}
          </span>
          <span className="text-foreground-400 flex items-center gap-1">
            <i className="ri-share-forward-line text-[10px]"></i>
            {formatearNumero(clip.compartidos)}
          </span>
          <span className="text-amber-600 font-semibold ml-auto flex items-center gap-0.5">
            <i className="ri-flashlight-fill text-[10px]"></i>
            {formatearNumero(clip.scoreTotal)}
          </span>
        </div>
      </div>
    </div>
  );
}

function FeaturedClipCard({ clip }: { clip: Clip }) {
  const fuenteInfo = fuenteLabels[clip.fuente];
  const durInfo = duracionLabels[clip.duracion];

  return (
    <div className="rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-300 cursor-pointer">
      <div className="relative h-52 overflow-hidden">
        <img
          src={clip.imagenUrl}
          alt={clip.titulo}
          className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center">
            <i className="ri-play-fill text-foreground-950 text-2xl ml-0.5"></i>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2.5 py-1 rounded-md ${durInfo.bg} ${durInfo.color} text-[9px] font-bold`}>
            {durInfo.label} · {durInfo.descripcion}
          </span>
        </div>
        <div className="absolute top-3 left-3 flex items-center gap-1.5">
          {clip.esTendencia && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-500 text-white text-[9px] font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
              TENDENCIA
            </span>
          )}
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md ${fuenteInfo.color} text-[9px] font-semibold`}>
            <i className={`${fuenteInfo.icono} text-[9px]`}></i>
            {fuenteInfo.label}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <p className="text-white font-bold text-sm leading-snug line-clamp-2">{clip.titulo}</p>
          <p className="text-white/70 text-[10px] mt-1">{clip.radioNombre}{clip.locutorNombre ? ` · ${clip.locutorNombre}` : ''}</p>
        </div>
      </div>
      <div className="p-4 space-y-2">
        <p className="text-[11px] text-foreground-600 leading-relaxed line-clamp-2">{clip.descripcion}</p>
        <div className="flex flex-wrap gap-1 pt-1.5 border-t border-background-200/70">
          {clip.tags.map(tag => (
            <span key={tag} className="px-1.5 py-0.5 rounded-md bg-background-100 text-[9px] text-foreground-500">#{tag}</span>
          ))}
        </div>
        <div className="flex items-center gap-3 text-[10px] text-foreground-400 pt-1">
          <span className="flex items-center gap-1"><i className="ri-eye-line text-[10px]"></i>{formatearNumero(clip.vistas)}</span>
          <span className="flex items-center gap-1"><i className="ri-share-forward-line text-[10px]"></i>{formatearNumero(clip.compartidos)}</span>
          <span className="text-amber-600 font-semibold ml-auto flex items-center gap-0.5">
            <i className="ri-flashlight-fill text-[10px]"></i>{formatearNumero(clip.scoreTotal)} pts
          </span>
        </div>
      </div>
    </div>
  );
}

export default function ClipsSection() {
  const [activeTab, setActiveTab] = useState<TabClips>('tendencia');

  const tendencia = getClipsEnTendencia().slice(0, 6);
  const masVistos = getMasVistos().slice(0, 6);
  const masCompartidos = getMasCompartidos().slice(0, 6);

  const tabs = [
    { id: 'tendencia' as const, label: 'Tendencia', icon: 'ri-fire-line', count: getClipsEnTendencia().length },
    { id: 'mas-vistos' as const, label: 'Más Vistos', icon: 'ri-eye-line', count: masVistos.length },
    { id: 'mas-compartidos' as const, label: 'Más Compartidos', icon: 'ri-share-forward-line', count: masCompartidos.length },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line', count: reglasClips.length },
  ];

  const currentClips = activeTab === 'tendencia' ? tendencia
    : activeTab === 'mas-vistos' ? masVistos
    : masCompartidos;

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1100px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            CLIPS
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
            Contenido Viral que Impulsa Descubrimiento
          </h2>
          <p className="text-foreground-500 text-sm max-w-[620px] mx-auto leading-relaxed">
            Clips de 15 segundos a 3 minutos sacados de programas, entrevistas, eventos, concursos y locutores. Cada clip es una puerta de entrada a nuevas radios y contenido.
          </p>
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
          {[
            { label: 'Clips', valor: globalStatsClips.totalClips.toString(), icono: 'ri-film-line', color: 'text-rose-600', bg: 'bg-rose-100' },
            { label: 'En Tendencia', valor: globalStatsClips.clipsEnTendencia.toString(), icono: 'ri-fire-line', color: 'text-amber-600', bg: 'bg-amber-100' },
            { label: 'Vistas Totales', valor: formatearNumero(globalStatsClips.totalVistas), icono: 'ri-eye-line', color: 'text-emerald-600', bg: 'bg-emerald-100' },
            { label: 'Compartidos', valor: formatearNumero(globalStatsClips.totalCompartidos), icono: 'ri-share-forward-line', color: 'text-violet-600', bg: 'bg-violet-100' },
          ].map((stat) => (
            <div key={stat.label} className="bg-background-50 rounded-2xl border border-background-200 p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${stat.bg} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${stat.icono} ${stat.color} text-sm`}></i>
              </div>
              <p className="text-foreground-950 font-bold text-lg">{stat.valor}</p>
              <p className="text-foreground-400 text-[10px]">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Source type pills */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 mb-6">
          {(Object.entries(fuenteLabels) as [string, typeof fuenteLabels[keyof typeof fuenteLabels]][]).map(([key, info]) => (
            <span
              key={key}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium ${info.color}`}
            >
              <i className={`${info.icono} text-[10px]`}></i>
              {info.label}
            </span>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-center mb-8">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'
                }`}>{tab.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {activeTab !== 'reglas' ? (
          <>
            {/* Featured cards for Tendencia */}
            {activeTab === 'tendencia' && currentClips.length >= 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                {(currentClips as Clip[]).slice(0, 2).map(clip => (
                  <FeaturedClipCard key={clip.id} clip={clip} />
                ))}
              </div>
            )}
            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(activeTab === 'tendencia'
                ? (currentClips as Clip[]).slice(2)
                : currentClips as Clip[]
              ).map(clip => (
                <ClipCard key={clip.id} clip={clip} />
              ))}
            </div>
          </>
        ) : (
          <>
            {/* Reglas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {reglasClips.map(regla => (
                <div key={regla.id} className="p-5 rounded-2xl bg-background-50 border border-background-200/70">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${regla.gradiente} flex items-center justify-center mb-3`}>
                    <i className={`${regla.icono} text-white text-lg`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1.5">{regla.titulo}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">{regla.descripcion}</p>
                </div>
              ))}
            </div>

            {/* Score */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {scoreAccionesClips.map(item => (
                <div key={item.id} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${item.icono} text-white text-sm`}></i>
                  </div>
                  <p className="text-lg font-extrabold text-foreground-950">+{item.puntos}</p>
                  <p className="text-[10px] font-semibold text-foreground-700">{item.accion}</p>
                  <p className="text-[9px] text-foreground-400 mt-1">{item.descripcion}</p>
                </div>
              ))}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="mt-10 text-center">
          <Link
            to="/clips"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-background-50 font-bold text-sm cursor-pointer hover:from-rose-600 hover:to-amber-600 transition-all whitespace-nowrap"
          >
            <i className="ri-film-line"></i>
            Ver Todos los Clips
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Regla final */}
        <div className="mt-8 text-center">
          <p className="text-foreground-400 text-xs italic">
            &ldquo;Los clips deben impulsar descubrimiento. Cada clip es una puerta de entrada a nuevas radios, programas y locutores.&rdquo;
          </p>
        </div>
      </div>
    </section>
  );
}